import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router";
import { ClipboardCheck, FileText, Plus, Search, ShieldCheck } from "lucide-react";
import { useToastStore } from "../../stores/toastStore";
import { RBACAction } from "../components/RBACButton";
import { PageShell } from "../components/layout/PageShell";
import { TableToolbar } from "../components/TableToolbar";
import { DataTable } from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";
import {
  EntityCardGrid,
  TitleTooltip,
  StatTiles,
  StatTile,
  MetaDates,
} from "../components/EntityCardGrid";
import {
  safeFormatDate,
  getPageSizeOptions,
  resolvePageSizeForViewMode,
} from "../../utils/formatters";
import {
  searchAssessmentPlans,
  ASSESSMENT_PLAN_SORT_COLUMNS,
  ApiAssessmentPlan,
  type AssessmentPlanStatus,
} from "../../api/frameworksApi";

export function AssessmentPlans() {
  const navigate = useNavigate();
  /* ── List state ──
   * Search, sorting, filtering and pagination are all resolved by the backend
   * via POST /assessment-plans/{page}/{size}; `plans` always holds exactly the
   * rows for the current page and is rendered as-is. */
  const [search, setSearch] = useState("");
  // The keyword actually sent to the backend (committed on Enter / icon click).
  const [keyword, setKeyword] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [viewMode, setViewMode] = useState<"card" | "table">("card");
  // Must be a valid option for the default view mode above ("card") — the
  // module-level PAGE_SIZE (10) isn't one of [12,24,48,96].
  const [pageSize, setPageSize] = useState(getPageSizeOptions("card")[0]);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(0);
  };

  const [plans, setPlans] = useState<ApiAssessmentPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The skeleton is only shown before the very first page ever lands; every
  // later fetch keeps the current rows visible (no blank page on paging).
  const firstLoadDoneRef = useRef(false);
  const fetchData = useCallback(async () => {
    if (!firstLoadDoneRef.current) setLoading(true);
    setError(null);
    try {
      const res = await searchAssessmentPlans(page, pageSize, {
        keyword: keyword.trim(),
        // Unsorted (the initial state) sends both sort fields as empty.
        sortByColumn: sortKey
          ? (ASSESSMENT_PLAN_SORT_COLUMNS[sortKey] ?? sortKey)
          : "",
        sortingOrder: sortKey ? sortDir : "",
        status: statusFilter === "all" ? null : statusFilter,
      });
      setPlans(res.items);
      setTotal(res.totalItems);
      // The current page can fall off the end of the result set (a delete, a
      // larger page size, a narrower filter) — step back to the last real page.
      if (res.items.length === 0 && page > 0 && res.totalItems > 0) {
        setPage(Math.max(0, Math.ceil(res.totalItems / pageSize) - 1));
      }
    } catch (e: any) {
      setError(e?.message ?? "Failed to load assessment plans");
      setPlans([]);
      setTotal(0);
    } finally {
      firstLoadDoneRef.current = true;
      setLoading(false);
    }
  }, [page, pageSize, keyword, sortKey, sortDir, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ── Search submit ──
   * Typing alone never hits the API: the keyword is only pushed to the
   * backend on Enter / the search button, or the moment the box is cleared. */
  const submitSearch = (value: string) => {
    const next = value.trim();
    if (next === keyword) return;
    setKeyword(next);
    setPage(0);
  };

  useEffect(() => {
    if (error) useToastStore.getState().showError("Error", error);
  }, [error]);

  const resolveSspTitle = (p: ApiAssessmentPlan): string | null => {
    if (p.sspTitle) return p.sspTitle;
    if (!p.importSspHref) return null;
    return p.importSspHref.split("/").pop() ?? p.importSspHref;
  };

  const SortIcon = ({ col }: { col: string }) => {
    const active = sortKey === col;
    return (
      <span
        className="inline-flex flex-col items-center ml-2 select-none"
        style={{ fontSize: "7px", lineHeight: "7px" }}
      >
        <span
          className={`font-black ${active && sortDir === "asc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
        >
          ▲
        </span>
        <span
          className={`font-black ${active && sortDir === "desc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
        >
          ▼
        </span>
      </span>
    );
  };

  const displayTitle = (p: ApiAssessmentPlan) =>
    p.title ?? `Assessment Plan ${p.id.slice(-8)}`;

  const openPlan = (p: ApiAssessmentPlan) => {
    const status = p.status ?? (p.lifecycleStatus as AssessmentPlanStatus);
    if (status === "draft") {
      navigate(`/continuous-compliance/assessment/assessment-plan/wizard?planId=${p.id}`);
    } else {
      navigate(`/continuous-compliance/assessment/assessment-plan/${p.id}`);
    }
  };

  /* ── Rows for the current page ──
   * No client-side filtering, sorting or slicing: the backend already applied
   * the keyword, sort column/order, status filter and page window. */
  const paged = plans;

  return (
    <PageShell className="flex flex-col flex-1 min-h-0 gap-6">
      {/* Header */}
      <div className="flex-shrink-0 flex items-start justify-between gap-4">
        <div>
          <h1>Assessment Plan</h1>
          <p className="text-[14px] text-[#94a3b8] dark:text-white/70">
            Manage security control assessment plans linked to SSPs
          </p>
        </div>
        <RBACAction module="assessment_plan" action="create">
          <button
            onClick={() => navigate("/continuous-compliance/assessment/assessment-plan/wizard")}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20 flex-shrink-0"
          >
            <Plus className="size-4" />
            New Assessment Plan
          </button>
        </RBACAction>
      </div>

      {/* Stats */}
      {/* <div className="grid grid-cols-3 gap-3">
        {loading
          ? Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="rounded-lg border border-border bg-card p-3 text-center">
                <Skeleton className="h-8 w-12 mx-auto mb-1.5" />
                <Skeleton className="h-3 w-20 mx-auto" />
              </div>
            ))
          : [
              { label: 'Total Plans', value: plans.length, bg: 'bg-indigo-50 dark:bg-indigo-500/[0.08]', border: 'border-indigo-200 dark:border-indigo-500/20', valueColor: 'text-indigo-600 dark:text-indigo-400', labelColor: 'text-indigo-600/70 dark:text-indigo-400/70' },
              { label: 'Linked to SSP', value: plans.filter(p => p.importSspHref).length, bg: 'bg-indigo-50 dark:bg-indigo-500/[0.08]', border: 'border-indigo-200 dark:border-indigo-500/20', valueColor: 'text-indigo-600 dark:text-indigo-400', labelColor: 'text-indigo-600/70 dark:text-indigo-400/70' },
              { label: 'Unlinked', value: plans.filter(p => !p.importSspHref).length, bg: 'bg-amber-50 dark:bg-amber-500/[0.08]', border: 'border-amber-200 dark:border-amber-500/20', valueColor: 'text-amber-600 dark:text-amber-400', labelColor: 'text-amber-600/70 dark:text-amber-400/70' },
            ].map(({ label, value, bg, border, valueColor, labelColor }) => (
              <div key={label} className={`rounded-lg border ${border} ${bg} p-3 text-center`}>
                <p className={`text-2xl font-semibold ${valueColor}`}>{value}</p>
                <p className={`text-xs font-medium mt-0.5 ${labelColor}`}>{label}</p>
              </div>
            ))}
      </div> */}

      {/* Error */}
      {error && (
        <div className="flex-shrink-0 flex items-center gap-2 px-4 py-3 rounded-lg border border-border bg-card text-[14px] text-[#94a3b8] dark:text-white/70">
          <span>Something went wrong.</span>
          <button
            onClick={fetchData}
            className="ml-auto text-xs underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      <EntityCardGrid<ApiAssessmentPlan>
        items={paged}
        loading={loading}
        getKey={(p) => p.id}
        getStatus={(p) => p.status ?? p.lifecycleStatus ?? "draft"}
        onOpen={openPlan}
        page={page}
        pageSize={pageSize}
        totalItems={total}
        onPageChange={setPage}
        onPageSizeChange={(s) => {
          setPageSize(s);
          setPage(0);
        }}
        pageSizeOptions={[...getPageSizeOptions(viewMode)]}
        itemLabel="plans"
        emptyText={
          plans.length === 0
            ? "No assessment plans yet"
            : "No plans match your search"
        }
        emptyIcon={
          <Search className="size-4 text-[#64748b] dark:text-white/50" />
        }
        viewMode={viewMode}
        onViewModeChange={(mode) => {
          setViewMode(mode);
          setPageSize(resolvePageSizeForViewMode(pageSize, mode));
          setPage(0);
        }}
        toolbarLeft={
          <TableToolbar
            search={{
              value: search,
              onChange: setSearch,
              onSubmit: submitSearch,
              placeholder: "Search by SSP Name or Plan Name",
              ariaLabel: "Search assessment plans",
            }}
            filters={[
              {
                key: "status",
                value: statusFilter,
                onChange: (v) => {
                  setStatusFilter(v);
                  setPage(0);
                },
                className: "min-w-[180px]",
                options: [
                  { value: "all", label: "All Status" },
                  { value: "draft", label: "Draft", dot: "bg-slate-400" },
                  { value: "in_review", label: "In Review", dot: "bg-blue-500" },
                  { value: "approved", label: "Approved", dot: "bg-emerald-500" },
                  { value: "rejected", label: "Rejected", dot: "bg-red-500" },
                ],
              },
            ]}
          />
        }
        renderTitle={(p) => (
          <TitleTooltip text={displayTitle(p)}>
            <h3 className="text-[13px] font-bold leading-snug tracking-[-0.01em] text-foreground truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
              {displayTitle(p)}
            </h3>
          </TitleTooltip>
        )}
        renderHeaderRight={() => (
          <ClipboardCheck className="size-4 text-indigo-500 dark:text-indigo-400" />
        )}
        renderBadges={(p) =>
          resolveSspTitle(p) && (
            <span className="inline-flex items-center gap-1 whitespace-nowrap max-w-full truncate px-1.5 py-0.5 rounded-md text-[10px] font-medium border bg-indigo-50 text-indigo-700 border-indigo-200/70 dark:bg-indigo-500/10 dark:text-indigo-300 dark:border-indigo-500/20">
              <FileText className="size-3 flex-shrink-0" />
              <span className="truncate">{resolveSspTitle(p)}</span>
            </span>
          )
        }
        renderStats={(p) => (
          <StatTiles>
            <StatTile
              icon={<ShieldCheck className="size-3.5" />}
              accent="indigo"
              value={p.controlCount ?? 0}
              label="Controls"
            />
          </StatTiles>
        )}
        renderDates={(p) => (
          <MetaDates
            created={safeFormatDate(p.startDate)}
            updated={safeFormatDate(p.endDate)}
            createdLabel="Start"
            updatedLabel="End"
          />
        )}
        renderTable={(rows) => (
          <DataTable<ApiAssessmentPlan>
            columns={[
              {
                key: "no",
                header: "#",
                align: "center",
                render: (_row, idx) => (
                  <span className="text-[#0f172a] dark:text-white/80 font-medium text-[12px]">
                    {page * pageSize + idx + 1}
                  </span>
                ),
              },
              {
                key: "name",
                align: "left",
                ellipsis: 2,
                tooltip: (p) => displayTitle(p) || "",
                header: (
                  <button
                    onClick={() => handleSort("name")}
                    className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    Plan Name
                    <SortIcon col="name" />
                  </button>
                ),
                render: (p) => (
                  <p className="font-medium text-[#0f172a] dark:text-white/80">
                    {displayTitle(p)}
                  </p>
                ),
              },
              {
                key: "ssp",
                align: "left",
                ellipsis: 2,
                tooltip: (p) => resolveSspTitle(p) || "",
                header: (
                  <button
                    onClick={() => handleSort("ssp")}
                    className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    Linked SSP
                    <SortIcon col="ssp" />
                  </button>
                ),
                render: (p) =>
                  resolveSspTitle(p) ? (
                    <span className="text-[13px] font-medium text-[#0f172a] dark:text-white/80">
                      {resolveSspTitle(p)}
                    </span>
                  ) : (
                    <span className="text-[13px] font-medium text-[#0f172a] dark:text-white/80">
                      -
                    </span>
                  ),
              },
              {
                key: "status",
                header: (
                  <button
                    onClick={() => handleSort("status")}
                    className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    Status
                    <SortIcon col="status" />
                  </button>
                ),
                align: "center",
                render: (p) => {
                  const s = p.status ?? (p.lifecycleStatus as AssessmentPlanStatus);
                  if (s === "approved") {
                    return (
                      <span className="inline-flex items-center justify-center w-[110px] px-3 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-800 ring-1 ring-emerald-300 dark:bg-emerald-500/20 dark:text-emerald-300 dark:ring-emerald-500/30">
                        Approved
                      </span>
                    );
                  }
                  return <StatusBadge status={s} />;
                },
              },
              {
                key: "controls",
                align: "center",
                header: (
                  <button
                    onClick={() => handleSort("controls")}
                    className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    Controls
                    <SortIcon col="controls" />
                  </button>
                ),
                render: (p) => (
                  <span className="text-[#0f172a] dark:text-white/80 font-medium">
                    {p.controlCount ?? 0}
                  </span>
                ),
              },
              {
                key: "startDate",
                align: "center",
                header: (
                  <button
                    onClick={() => handleSort("startDate")}
                    className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    Start Date
                    <SortIcon col="startDate" />
                  </button>
                ),
                render: (p) => (
                  <span className="whitespace-nowrap font-medium text-[#0f172a] dark:text-white/80">
                    {p.startDate
                      ? new Date(p.startDate).toLocaleDateString("en-GB")
                      : "-"}
                  </span>
                ),
              },
              {
                key: "endDate",
                align: "center",
                header: (
                  <button
                    onClick={() => handleSort("endDate")}
                    className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    End Date
                    <SortIcon col="endDate" />
                  </button>
                ),
                render: (p) => (
                  <span className="whitespace-nowrap font-medium text-[#0f172a] dark:text-white/80">
                    {p.endDate
                      ? new Date(p.endDate).toLocaleDateString("en-GB")
                      : "-"}
                  </span>
                ),
              },
            ]}
            data={rows}
            loading={loading}
            error={error}
            onRetry={fetchData}
            emptyText={
              plans.length === 0
                ? "No assessment plans yet"
                : "No plans match your search"
            }
            emptyIcon={
              <Search className="size-4 text-[#64748b] dark:text-white/50" />
            }
            onRowClick={openPlan}
            rowKey={(p) => p.id}
            itemLabel="plans"
          />
        )}
      />
    </PageShell>
  );
}
